import { useEffect } from "react";

import { CircularProgress, Stack } from "@mui/material";
import { usePostHog } from "posthog-js/react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";

import { NAV_ROUTES } from "components/NavigationBar";
import {
  CARRIER_PROFILE_SOURCE_QUERY_PARAM,
  CarrierProfileSource,
  ClientAppEvent,
} from "constants/index";
import { useAuthContext, useRoleContext } from "hooks";

import { CompanyProfilePageLayout } from "./CompanyProfilePageLayout";
import { parseCompanyIdOrNull } from "./utils";

// Only sources the app actually links with are trusted: the query param is
// arbitrary user input, so an unrecognised value reports as a direct visit
// rather than creating a junk breakdown value in PostHog.
function parseProfileSource(rawSource: string | null): CarrierProfileSource {
  const isKnownSource = Object.values(CarrierProfileSource).some(
    (source) => source === rawSource,
  );

  return isKnownSource
    ? (rawSource as CarrierProfileSource)
    : CarrierProfileSource.DIRECT;
}

export function CompanyProfilePage() {
  const { isLoading: isAuthLoading } = useAuthContext();
  const { company, role, isLoading: isRoleLoading } = useRoleContext();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const postHog = usePostHog();

  const companyId = parseCompanyIdOrNull(params.id);
  const profileSource = parseProfileSource(
    searchParams.get(CARRIER_PROFILE_SOURCE_QUERY_PARAM),
  );

  /*
  shippers viewing their own company are redirected below: shipper companies do
  not have a profile.
  */
  const isOwnShipperCompany =
    role?.type === "SHIPPER" && company?.id === companyId;

  /*
  Gated on the same conditions as the render below, so a view that resolves to a
  spinner, nothing, or a redirect is not reported as a profile view. The role
  context resolves separately from auth and starts out empty, so waiting on it
  too is what keeps a shipper's own company from counting as a carrier view.
  */
  const isViewingCarrierProfile =
    !isAuthLoading &&
    !isRoleLoading &&
    companyId != null &&
    !isOwnShipperCompany;

  useEffect(() => {
    if (!isViewingCarrierProfile) {
      return;
    }

    postHog?.capture(ClientAppEvent.CARRIER_PROFILE_VIEWED, {
      company_id: companyId,
      profile_source: profileSource,
    });
  }, [isViewingCarrierProfile, companyId, profileSource, postHog]);

  if (isAuthLoading || isRoleLoading) {
    return (
      <Stack alignItems="center">
        <CircularProgress />
      </Stack>
    );
  }

  if (companyId == null) {
    return null;
  }

  if (isOwnShipperCompany) {
    return <Navigate replace to={NAV_ROUTES.companyAdmin(companyId)} />;
  }

  return <CompanyProfilePageLayout companyId={companyId} />;
}
